import { useState } from "react";
import { Box, Button, Modal, Stack, Typography } from "@mui/material";
import { Icon } from "@iconify/react";
import { useNavigate } from "react-router-dom";
import InitiativeDeleteModal from "./deleteModal";
import InitiativeStatusModal from "./statusModal";
import { AntSwitch } from "../../../components/antSwitch";
import { tableDeleteButtonSx, tableViewButtonSx } from "../../../styles/actionStyles";
import { primaryIconColor } from "../../../styles/iconStyles";

export default function InitiativeColumn(props) {

  const navigate = useNavigate();
  const { setAlert, setOpenAlert, setResponseMsg, setLoading, getInitiativeData, expandedRows, onToggleDescription } = props;
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [openStatusModal, setOpenStatusModal] = useState(false);
  const ALERT_DURATION = 2000;
  const totalColumns = 5;
  const alertClose = () => {
    setAlert('')
  }
  const handleOpenDeleteModal = (id, status) => {
    setSelectedId(id);
    setSelectedStatus(status);
    setOpenDeleteModal(true);
  };

  const handleOpenStatusModal = (id, status) => {
    setSelectedId(id);
    setSelectedStatus(status);
    setOpenStatusModal(true);
  };

  const handleCloseDeleteModal = () => {
    setOpenDeleteModal(false);
  };


  const handleCloseStatusModal = () => {
    setOpenStatusModal(false);
  };

  const initiativeColumnColumn = [
    {
      field: 'name',
      headerName: 'Initiative Name',
      flex: 1.1,
      minWidth: 210,
      colSpan: (params) => params?.row?.__detail ? totalColumns : 1,
      renderCell: (params) => (
        params.row.__detail ? (
          <Box
            sx={{
              width: '100%',
              mx: 2,
              mb: 1.5,
              px: 3,
              py: 2.5,
              backgroundColor: '#FFFFFF',
              borderRadius: '10px',
              boxShadow: '0px 10px 24px rgba(31, 46, 85, 0.10)',
            }}
          >
            <Typography
              sx={{
                color: '#1C1B1F',
                fontSize: 16,
                lineHeight: '24px',
                letterSpacing: '0.15px',
                whiteSpace: 'normal',
              }}
            >
              {params.row.fullDescription}
            </Typography>
          </Box>
        ) : (
          <Typography sx={{ color: '#1C1B1F', fontSize: 16, lineHeight: '24px', letterSpacing: '0.15px' }}>
            {params.value}
          </Typography>
        )
      ),
    },
    {
      field: 'description',
      headerName: 'Description',
      flex: 1.2,
      minWidth: 260,
      colSpan: (params) => params?.row?.__detail ? 0 : 1,
      renderCell: (params) => {
        if (params.row.__detail) return null;
        const htmlString = params.value || "";
        const plainText = htmlString.replace(/<[^>]+>/g, '').replace(/\n/g, ' ');
        const isExpanded = expandedRows.has(params.row.id);
        return (
          <Stack direction="row" alignItems="center" spacing={1} sx={{ width: '100%' }}>
            <Typography
              sx={{
                color: '#1C1B1F',
                fontSize: 16,
                lineHeight: '24px',
                letterSpacing: '0.15px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {plainText}
            </Typography>
            <Button
              onClick={(event) => {
                event.stopPropagation();
                onToggleDescription(params.row);
              }}
              sx={{
                minWidth: 'auto',
                p: 0,
                color: '#1C1B1F',
                '&:hover': {
                  backgroundColor: 'transparent',
                },
              }}
            >
              <Icon icon={isExpanded ? "mdi:chevron-up" : "mdi:chevron-down"} width="20" height="20" color={primaryIconColor} />
            </Button>
          </Stack>
        );
      },
    },
    {
      field: 'countryName',
      headerName: 'Country',
      flex: 0.75,
      minWidth: 140,
      colSpan: (params) => params?.row?.__detail ? 0 : 1,
      renderCell: (params) => params.row.__detail ? null : (
        <Typography sx={{ color: '#1C1B1F', fontSize: 16, lineHeight: '24px', letterSpacing: '0.15px' }}>
          {params?.row?.countryName ?? '-'}
        </Typography>
      ),
    },
    {
      field: "status",
      headerName: "Status",
      flex: 0.8,
      minWidth: 170,
      colSpan: (params) => params?.row?.__detail ? 0 : 1,
      renderCell: (params) => (
        params.row.__detail ? null : (
          <>
            <Stack direction="row" alignItems="center" spacing={1.25} sx={{ height: '100%' }}>
              <AntSwitch checked={params.row.status === "ACTIVE"} onChange={() => handleOpenStatusModal(params.row.id, params.row.status)} inputProps={{ "aria-label": "controlled" }} />
              <Typography sx={{ color: '#4F8506', fontSize: 16, lineHeight: '24px', letterSpacing: '0.15px', fontWeight: 500 }}>
                {params.row.status === "ACTIVE" ? 'Active' : 'Inactive'}
              </Typography>
            </Stack>

            <Modal open={openStatusModal} onClose={handleCloseStatusModal} BackdropProps={{ sx: { backgroundColor: "rgba(0, 0, 0, 0.1)" } }}>
              <div>
                <InitiativeStatusModal onClose={handleCloseStatusModal} getInitiativeData={getInitiativeData} selectedId={selectedId} selectedStatus={selectedStatus} alertClose={alertClose} setOpenAlert={setOpenAlert} setResponseMsg={setResponseMsg} setAlert={setAlert} ALERT_DURATION={ALERT_DURATION} setLoading={setLoading} />
              </div>
            </Modal>
          </>
        )
      ),
    },

    {
      field: "actions",
      headerName: "Action",
      flex: 0.95,
      minWidth: 220,
      sortable: false,
      colSpan: (params) => params?.row?.__detail ? 0 : 1,
      renderCell: (params) => (
        params.row.__detail ? null : (
        <>
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ width: '100%', justifyContent: 'flex-start' }}>
            <Button
              onClick={() => navigate(`/initiativeView/${params.row.id}?type=edit`)}
              startIcon={<Icon icon="mdi:eye-outline" width="18" height="18" />}
              sx={{ ...tableViewButtonSx, px: 2 }}
            >
              View
            </Button>
            <Button
              onClick={() => handleOpenDeleteModal(params.row.id, params.row.status)}
              startIcon={<Icon icon="mdi:delete-outline" width="18" height="18" />}
              sx={{ ...tableDeleteButtonSx, px: 2 }}
            >
              Delete
            </Button>
          </Stack>

            <Modal open={openDeleteModal} onClose={handleCloseDeleteModal} BackdropProps={{ sx: { backgroundColor: "rgba(0, 0, 0, 0.1)" } }} >
              <div>
                <InitiativeDeleteModal onClose={handleCloseDeleteModal} getInitiativeData={getInitiativeData} selectedId={selectedId} selectedStatus={selectedStatus} alertClose={alertClose} setOpenAlert={setOpenAlert} setResponseMsg={setResponseMsg} setAlert={setAlert} ALERT_DURATION={ALERT_DURATION} setLoading={setLoading} />
              </div>
            </Modal>
          </>
        )
      ),
    },
  ];

  return initiativeColumnColumn;
}
